import React, { useEffect, useState } from 'react';
import { FreemiumContainer, CreditsSection, PlanSection, UpgradeSection, CreditsText, PlanText, UpgradeText, Separator } from './style';
import CreditsIconImage from '../../static/icons/credit-icon.svg';
import SubscriptionPopup from './SubscriptionPopup';
import { getUpgradeOptions, getSubscriptionTier, canHaveUnlimitedCredits, getRemainingCredits, getTotalCredits } from '../../utils/userAccessHelper';
import { useProfile } from '../../components/state/hooks.js';
import Loader from '../../components/Loader';
import { useTeacherLoopMe } from '../../hooks/useTeacherLoopMe';

// Teacher Loop (A5/A6): what the pill adds comes from a chunk loaded only once
// the server has enabled the feature for this student. The import sits behind
// the flag literal, so a flag-off build drops it (utils/teacherLoopGate.js).
const loadTeacherLoopPill = () =>
  process.env.REACT_APP_TEACHER_LOOP === 'on'
    ? import('../../components/TeacherLoop/TeacherLoopCreditPill')
    : Promise.resolve(null);
const NO_TEACHER_LOOP_PILL = Object.freeze({
  bonusCredits: 0,
  thanksCredits: 0,
  unlimitedUntil: null,
  parts: null,
});

const useTeacherLoopPill = (me, profile) => {
  const [parts, setParts] = useState(null);
  const wanted =
    process.env.REACT_APP_TEACHER_LOOP === 'on' && me?.enabled === true;
  useEffect(() => {
    if (!wanted || parts) return undefined;
    let live = true;
    loadTeacherLoopPill()
      .then((module) => {
        if (live && module) setParts(module);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [wanted, parts]);
  if (!wanted || !parts) return NO_TEACHER_LOOP_PILL;
  return { ...parts.teacherLoopPillState(me, profile), parts };
};

const FreemiumButton = ({ onUpgradeClick }) => {
  const { data: profile, isLoadingdata: isLoadingProfile } = useProfile();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // Teacher Loop: with the build flag off this returns { me: null } and makes no
  // request, so the pill below renders exactly as before.
  const { me: teacherLoopMe } = useTeacherLoopMe();
  const teacherLoopPill = useTeacherLoopPill(teacherLoopMe, profile);

  if (isLoadingProfile) {
    return <Loader />;
  }

  const isUnlimitedCredits = canHaveUnlimitedCredits(profile);
  const sku = profile?.subscriptionDetails?.sku;
  const upgradeOptions = getUpgradeOptions(profile);
  const hasValidUpgradeOptions = upgradeOptions.length > 0 && !upgradeOptions.includes('NONE') && !upgradeOptions.includes('ALREADY_AT_HIGHEST');

  const remainingCredits = getRemainingCredits(profile);
  const totalCredits = getTotalCredits(profile);
  const displayPlan = getSubscriptionTier(profile);

  if (!sku && !hasValidUpgradeOptions && !isUnlimitedCredits) {
    return null;
  }

  const handleUpgradeClick = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    }
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  const handleStartUsingJeddAI = () => {
  };

  if (teacherLoopPill.unlimitedUntil) {
    const { TeacherLoopUnlimitedSegments } = teacherLoopPill.parts;
    return (
      <FreemiumContainer
        isUnlimited={false}
        data-testid="teacher-loop-credit-pill"
      >
        <TeacherLoopUnlimitedSegments
          untilLabel={teacherLoopPill.unlimitedUntil}
          Separator={Separator}
        />
      </FreemiumContainer>
    );
  }
  const TeacherLoopBonusTag = teacherLoopPill.parts?.TeacherLoopBonusTag;
  const TeacherLoopThanksTag = teacherLoopPill.parts?.TeacherLoopThanksTag;

  return (
    <>
      <FreemiumContainer isUnlimited={isUnlimitedCredits}>
        {!isUnlimitedCredits && (
          <CreditsSection>
            <img src={CreditsIconImage} />
            <CreditsText>{`${remainingCredits} of ${totalCredits} credits remaining`}</CreditsText>
            {teacherLoopPill.bonusCredits > 0 && TeacherLoopBonusTag && (
              <TeacherLoopBonusTag credits={teacherLoopPill.bonusCredits} />
            )}
            {teacherLoopPill.thanksCredits > 0 && TeacherLoopThanksTag && (
              <TeacherLoopThanksTag credits={teacherLoopPill.thanksCredits} />
            )}
          </CreditsSection>
        )}

        {(sku || hasValidUpgradeOptions) && displayPlan !== 'Plus' && !isUnlimitedCredits && (
          <>
            <Separator />
            <PlanSection>
              <PlanText>{displayPlan}</PlanText>
            </PlanSection>
          </>
        )}
        {(() => {
          const showUpgrade = !upgradeOptions.includes('NONE') && !upgradeOptions.includes('ALREADY_AT_HIGHEST') && upgradeOptions.length > 0;
          return showUpgrade ? (
            <>
               {displayPlan !== 'Plus' && <Separator />}
              <UpgradeSection onClick={handleUpgradeClick}>
                <UpgradeText>Upgrade</UpgradeText>
              </UpgradeSection>
            </>
          ) : null;
        })()}
      </FreemiumContainer>

      <SubscriptionPopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        title="JeddAI Pro subscription has been added to your account"
        description="365 days remaining in your subscription."
        buttonText="Start using JeddAI"
        onButtonClick={handleStartUsingJeddAI}
      />
    </>
  );
};

export default FreemiumButton;


